/**
 * Local-only E2E auth bootstrap. Enabled exclusively by
 * REACT_APP_LOCAL_E2E_AUTH=true. Never enable in production builds.
 *
 * API requests carry Authorization: Bearer local-e2e:<uid> (see services/api.js) and this exposes a
 * synthetic signed-in user so Scenario Modeling / BondReady routes render
 * without Firebase Admin ADC.
 */
const ENABLED = String(process.env.REACT_APP_LOCAL_E2E_AUTH || '').toLowerCase() === 'true';
const STORAGE_KEY = 'constructpay.localE2e.uid';
const TEST_RUN_KEY = 'constructpay.localE2e.testRunId';

export function isLocalE2EAuthEnabled() {
  return ENABLED;
}

// No built-in identity: local E2E must supply REACT_APP_LOCAL_E2E_UID or ?localE2eUid=.
export function getLocalE2EUid() {
  if (typeof window === 'undefined') return process.env.REACT_APP_LOCAL_E2E_UID || '';
  return (
    window.sessionStorage.getItem(STORAGE_KEY)
    || process.env.REACT_APP_LOCAL_E2E_UID
    || ''
  );
}

export function setLocalE2EUid(uid) {
  if (typeof window === 'undefined') return;
  if (uid) window.sessionStorage.setItem(STORAGE_KEY, uid);
  else window.sessionStorage.removeItem(STORAGE_KEY);
}

/**
 * Local E2E / manual QA correlation id. Production builds always return null.
 * When local E2E auth is enabled, may come from ?localTestRunId= query
 * (SAYRICK-BOND-E2E|MANUAL-*) or prior sessionStorage — never from hardcoded
 * opportunity UUID maps.
 */
export function resolveLocalTestRunId() {
  if (!isLocalE2EAuthEnabled() || typeof window === 'undefined') return null;
  const fromQuery = new URLSearchParams(window.location.search).get('localTestRunId');
  if (fromQuery && /^SAYRICK-BOND-(E2E|MANUAL)-/.test(fromQuery)) {
    window.sessionStorage.setItem(TEST_RUN_KEY, fromQuery);
    return fromQuery;
  }
  return window.sessionStorage.getItem(TEST_RUN_KEY);
}

export function localE2EBearerToken(uid = getLocalE2EUid()) {
  return `local-e2e:${uid}`;
}

export function createLocalE2EUser(uid) {
  const resolvedUid = uid || getLocalE2EUid();
  if (uid) setLocalE2EUid(uid);
  return {
    uid: resolvedUid,
    email: resolvedUid === 'local-e2e-holmes-uid'
      ? 'holmes-e2e@constructpay.local'
      : 'olympia-cfo@constructpay.ai',
    displayName: resolvedUid === 'local-e2e-holmes-uid' ? 'Holmes E2E Partner' : 'OlympiaTech CFO (local E2E)',
    getIdToken: async () => localE2EBearerToken(resolvedUid),
    getIdTokenResult: async () => ({
      token: localE2EBearerToken(resolvedUid),
      claims: { features: ['cfo_insights', 'bondready', 'gc_roi', 'scenarios'] },
    }),
  };
}

export const LOCAL_E2E_FEATURES = ['cfo_insights', 'bondready', 'gc_roi', 'scenarios'];
